import { useContext, useEffect, useRef, useState } from 'react';
import { useWindowSize } from '@leedpulse-monorepo/hooks';
import { ContextStatus, ETaskStatus, ETaskSourceType } from '@leedpulse-monorepo/enums';
import { Button, Empty, Modal, Table, Tabs, Tag } from 'antd';
import { doc, getDoc } from 'firebase/firestore';
import moment from 'moment';
import ReactMarkdown from 'react-markdown';
import TasksContext from '../../contexts/data/TasksContext';
import Task from '../../../domain/models/Task';
import { firestore, FirestoreCollections } from '../../../firebase.config';

interface CallDetail {
  customer: string;
  sender: string;
  duration: number;
  transcription: string;
  direction: string;
  created: number;
}

const formatDuration = (seconds: number): string => {
  if (!seconds || seconds <= 0) return '-';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}min ${s}s` : `${s}s`;
};

const normalizeTranscription = (value: unknown): string => {
  if (typeof value === 'string') return value;
  if (value && typeof value === 'object') {
    return Object.values(value as Record<string, unknown>)
      .map((v) => (typeof v === 'string' ? v : JSON.stringify(v)))
      .join(' — ');
  }
  return '-';
};

interface TasksListProps {
  scopedContactId: string | null;
  status: ETaskStatus;
  items: Task[];
  count: number;
}

const isUnscheduled = (timestamp: Date) => moment(timestamp).year() === 1970;

const SOURCE_TYPE_LABELS: Record<string, string> = {
  manual: 'Manuel',
  call: 'Appel',
  voicemail: 'Message vocal',
  sms: 'SMS',
};

const CALENDAR_SYNC_STATUS_LABELS: Record<string, string> = {
  synced: 'Synchronisée',
  pending: 'En attente',
  error: 'Erreur',
};

const CALENDAR_SYNC_STATUS_COLORS: Record<string, string> = {
  synced: 'green',
  pending: 'orange',
  error: 'red',
};

const TasksList: React.FC<TasksListProps> = ({
  scopedContactId,
  status,
  items,
  count
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [currentData, setCurrentData] = useState([] as Task[]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [relatedCall, setRelatedCall] = useState<CallDetail | null>(null);
  const [relatedCallLoading, setRelatedCallLoading] = useState(false);
  const tasksContext = useContext(TasksContext);
  const windowSize = useWindowSize();
  const fetchNextCalled = useRef(false);

  const handleViewDetails = (record: Task) => {
    setSelectedTask(record);
    setIsModalVisible(true);
    setRelatedCall(null);
    if (record.sourceType === ETaskSourceType.Call && record.sourceId) {
      setRelatedCallLoading(true);
      getDoc(doc(firestore, FirestoreCollections.calls, record.sourceId))
        .then((snapshot) => {
          if (snapshot.exists()) {
            setRelatedCall(snapshot.data() as CallDetail);
          }
        })
        .finally(() => setRelatedCallLoading(false));
    }
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
  };

  const handlePaginationChange = (page: number) => {
    setCurrentPage(page);
    const currentSlice = items.slice(
      (page - 1) * tasksContext.hitsPerPage,
      page * tasksContext.hitsPerPage
    );
    if (currentSlice.length === 0) {
      fetchNextCalled.current = true;
      tasksContext.fetchNext(status, scopedContactId);
    }
  };

  const handlePageSizeChange = (_current: number, newPageSize: number) => {
    setCurrentPage(1);
    tasksContext.fetch(status, scopedContactId, newPageSize);
  };

  // This hook is used to initialize the jobs list
  useEffect(() => {
    if (
      tasksContext.status === ContextStatus.IDLE ||
      tasksContext.scopedContactId !== scopedContactId
    ) {
      tasksContext.fetch(status, scopedContactId);
    }
    return () => {
      tasksContext.reset();
    };
  }, []);

  // This hook is triggered when a 'page next' is called to get the new slice of items
  useEffect(() => {
    if (tasksContext.status === ContextStatus.SUCCESS) {
      const currentSlice = items.slice(
        (currentPage - 1) * tasksContext.hitsPerPage,
        currentPage * tasksContext.hitsPerPage
      );
      // eslint-disable-next-line react/set-state-in-effect -- syncs the visible page slice once the async fetch (external system) resolves
      setCurrentData(currentSlice);
      fetchNextCalled.current = false;
    }
  }, [tasksContext.status]);

  // This hook is triggered when the current page changes to update the displayed data
  useEffect(() => {
    if (!fetchNextCalled.current) {
      const currentSlice = items.slice(
        (currentPage - 1) * tasksContext.hitsPerPage,
        currentPage * tasksContext.hitsPerPage
      );
      setCurrentData(currentSlice);
    }
  }, [currentPage]);

  // This hook is used to go back to first page when refreshing the list
  useEffect(() => {
    if (tasksContext.status === ContextStatus.SUCCESS) {
      if (
        items.slice(
          (currentPage - 1) * tasksContext.hitsPerPage,
          currentPage * tasksContext.hitsPerPage
        ).length === 0
      ) {
        // eslint-disable-next-line react/set-state-in-effect -- resets pagination once the refreshed list (external system) makes the current page empty
        setCurrentPage(1);
      }
    }
  }, [tasksContext.status]);

  return (
    <>
      <Table
        pagination={{
          current: currentPage,
          total: count,
          pageSize: tasksContext.hitsPerPage,
          onChange: handlePaginationChange,
          onShowSizeChange: (_current, newSize) => handlePageSizeChange(_current, newSize),
          showSizeChanger: true,
            simple: true,
          pageSizeOptions: [20, 50, 100, 200],
          position: ['bottomRight', 'topRight'],
          disabled: tasksContext.status === ContextStatus.LOADING,
        }}
        loading={tasksContext.status === ContextStatus.LOADING}
        columns={
          windowSize.width > 576
            ? [
              {
                title: 'Titre',
                dataIndex: 'title',
                key: 'title',
                render: (title: string) => (
                  <div>
                    <div>{title}</div>
                  </div>
                ),
              },
              {
                title: 'Description',
                dataIndex: 'description',
                key: 'description',
                render: (description: string | null) => description || '—',
              },
              {
                title: 'Date',
                dataIndex: 'timestamp',
                key: 'timestamp',
                render: (timestamp: Date) =>
                  isUnscheduled(timestamp) ? 'Non planifié' : moment(timestamp).format('LLL'),
              },
              {
                title: 'Créée le',
                dataIndex: 'created',
                key: 'created',
                render: (created: Date) => moment(created).format('LLL'),
              },
              {
                title: 'Modifiée le',
                dataIndex: 'updated',
                key: 'updated',
                render: (updated: Date) => moment(updated).format('LLL'),
              }
            ]
            : [
              {
                title: 'Titre',
                dataIndex: 'title',
                key: 'title',
              },
              {
                title: 'Description',
                dataIndex: 'description',
                key: 'description',
                render: (description: string | null) => description || '—',
              },
              {
                title: 'Date',
                dataIndex: 'timestamp',
                key: 'timestamp',
                render: (timestamp: Date) =>
                  isUnscheduled(timestamp) ? 'Non planifié' : moment(timestamp).format('LLL'),
              },
              {
                title: 'Créée le',
                dataIndex: 'created',
                key: 'created',
                render: (created: Date) => moment(created).format('LLL'),
              },
              {
                title: 'Modifiée le',
                dataIndex: 'updated',
                key: 'updated',
                render: (updated: Date) => moment(updated).format('LLL'),
              }
            ]
        }
        rowKey={(record) => record.id}
        dataSource={currentData}
        onRow={(record) => ({
          onClick: () => handleViewDetails(record),
          style: { cursor: 'pointer' },
        })}
        locale={{ emptyText: <Empty description='Aucune tâche disponible'></Empty> }}
      />
      <Modal
        title="Détails de la tâche"
        open={isModalVisible}
        onCancel={handleCloseModal}
        destroyOnHidden={true}
        footer={[
          <Button key="close" onClick={handleCloseModal}>
            Fermer
          </Button>,
        ]}
      >
        {selectedTask && (
          <Tabs defaultActiveKey='1'>
            <Tabs.TabPane key={1} tab='Détails'>
            <div style={{ marginBottom: '16px' }}>
              <strong>Titre :</strong>
              <div style={{ marginTop: '4px' }}>{selectedTask.title}</div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Description :</strong>
              <div style={{ marginTop: '4px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {selectedTask.description || 'Aucune description'}
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Statut :</strong>
              <div style={{ marginTop: '4px' }}>
                <Tag color={selectedTask.status === ETaskStatus.Completed ? 'green' : 'blue'}>
                  {selectedTask.status === ETaskStatus.Completed ? 'Terminée' : 'À faire'}
                </Tag>
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Échéance :</strong>
              <div style={{ marginTop: '4px' }}>
                {isUnscheduled(selectedTask.timestamp)
                  ? 'Non planifié'
                  : moment(selectedTask.timestamp).format('LLL')}
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Date de fin :</strong>
              <div style={{ marginTop: '4px' }}>
                {isUnscheduled(selectedTask.endTimestamp)
                  ? 'Non planifié'
                  : moment(selectedTask.endTimestamp).format('LLL')}
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Type de tâche :</strong>
              <div style={{ marginTop: '4px' }}>{selectedTask.taskType || 'Non renseigné'}</div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Catégorie (taxonomy) :</strong>
              <div style={{ marginTop: '4px' }}>{selectedTask.taxonomyKey || 'Non renseigné'}</div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Source :</strong>
              <div style={{ marginTop: '4px' }}>
                {selectedTask.sourceType ? SOURCE_TYPE_LABELS[selectedTask.sourceType] || selectedTask.sourceType : 'Non renseigné'}
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Lieu :</strong>
              <div style={{ marginTop: '4px' }}>{selectedTask.location || 'Non renseigné'}</div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Fuseau horaire :</strong>
              <div style={{ marginTop: '4px' }}>{selectedTask.timezone || 'Non renseigné'}</div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Synchronisation agenda :</strong>
              <div style={{ marginTop: '4px' }}>
                {selectedTask.calendarSync ? (
                  <>
                    <Tag color={CALENDAR_SYNC_STATUS_COLORS[selectedTask.calendarSync.sync_status]}>
                      {CALENDAR_SYNC_STATUS_LABELS[selectedTask.calendarSync.sync_status] || selectedTask.calendarSync.sync_status}
                    </Tag>
                    {selectedTask.calendarSync.html_link && (
                      <div style={{ marginTop: '4px' }}>
                        <a href={selectedTask.calendarSync.html_link} target="_blank" rel="noreferrer">
                          Voir dans Google Agenda
                        </a>
                      </div>
                    )}
                    {selectedTask.calendarSync.sync_status === 'error' && selectedTask.calendarSync.last_sync_error && (
                      <div style={{ marginTop: '4px', color: 'red' }}>
                        {selectedTask.calendarSync.last_sync_error}
                      </div>
                    )}
                  </>
                ) : (
                  'Non synchronisée'
                )}
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Créée le :</strong>
              <div style={{ marginTop: '4px' }}>
                {moment(selectedTask.created).format('LLL')}
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Modifiée le :</strong>
              <div style={{ marginTop: '4px' }}>
                {moment(selectedTask.updated).format('LLL')}
              </div>
            </div>
            <div style={{ marginBottom: '16px' }}>
              <strong>Notifiée :</strong>
              <div style={{ marginTop: '4px' }}>{selectedTask.notified ? 'Oui' : 'Non'}</div>
            </div>
            </Tabs.TabPane>
            {selectedTask.sourceType === ETaskSourceType.Call && selectedTask.sourceId && (
              <Tabs.TabPane key={2} tab='Appel lié'>
                {relatedCallLoading ? (
                  'Chargement...'
                ) : relatedCall ? (
                  <div>
                    <div style={{ marginBottom: '16px' }}>
                      <strong>Date :</strong>
                      <div style={{ marginTop: '4px' }}>
                        {new Date(relatedCall.created).toLocaleString('fr-FR', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                        })}
                      </div>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <strong>Type :</strong>
                      <div style={{ marginTop: '4px' }}>
                        {relatedCall.direction === 'inbound' ? 'Entrant' : 'Sortant'}
                      </div>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <strong>N° appelant :</strong>
                      <div style={{ marginTop: '4px' }}>{relatedCall.customer}</div>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <strong>N° appelé :</strong>
                      <div style={{ marginTop: '4px' }}>{relatedCall.sender}</div>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <strong>Durée :</strong>
                      <div style={{ marginTop: '4px' }}>{formatDuration(relatedCall.duration)}</div>
                    </div>
                    <div style={{ marginBottom: '16px' }}>
                      <strong>Résumé :</strong>
                      <div style={{ marginTop: '4px', wordBreak: 'break-word' }}>
                        <ReactMarkdown>{normalizeTranscription(relatedCall.transcription)}</ReactMarkdown>
                      </div>
                    </div>
                  </div>
                ) : (
                  'Appel introuvable'
                )}
              </Tabs.TabPane>
            )}
          </Tabs>
        )}
      </Modal>
    </>
  );
};

export default TasksList;
